import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
} from "react"

import { cn } from "@coval/ui/cn"

import {
  NO_LENS_VISUALIZER_LENS_KEY,
  type VisualizerConversation,
  type VisualizerLens,
} from "./uploadedConversationsVisualizerDataset"
import { UploadedConversationsVisualizerDotField } from "./UploadedConversationsVisualizerDotField"
import {
  easeInOutSine,
  easeOutCubic,
  LENS_RELEASE_PHASE_SHARE,
  LENS_TRANSITION_DURATION_MS,
} from "./uploadedConversationsVisualizerMotion"
import { createVisualizerMotionClock } from "./uploadedConversationsVisualizerMotionClock"
import { syncOverlayDotElements } from "./uploadedConversationsVisualizerOverlayDotDom"
import {
  resolveOverlayDotBlueprints,
  type OverlayDotBlueprint,
} from "./uploadedConversationsVisualizerOverlayDots"
import {
  resolveBlendedColor,
  resolveHoverPosition,
} from "./uploadedConversationsVisualizerOverlayHelpers"
import { UploadedConversationsVisualizerOverlayHoverCard } from "./UploadedConversationsVisualizerOverlayHoverCard"
import {
  clampNumber,
  clampToPlate,
  computeBackgroundChaosPoint,
  computeBackgroundScatterAnchor,
  computeBackgroundScatterPoint,
  projectToScreen,
  resolveCanvasColorPalette,
  type RgbColor,
} from "./uploadedConversationsVisualizerParticles"
import { type SetSlice } from "./UploadedConversationsVisualizerPieBackground"
import {
  buildClusterHoverZones,
  resolveHoveredBucketKeyFromZones,
  resolveUnderlayTransitionAlpha,
  upsertClusterUnderlayAggregate,
  type ClusterHoverZone,
  type ClusterUnderlayAggregate,
} from "./uploadedConversationsVisualizerUnderlay"
import {
  drawUnderlayInstruction,
  type UnderlayDrawInstruction,
} from "./uploadedConversationsVisualizerUnderlayCanvas"

interface UploadedConversationsVisualizerConversationOverlayProps {
  activeBucketKey?: string | null
  activeAnomalyMarkerColor?: string | null
  className?: string
  highlightedConversationIds?: ReadonlySet<string> | null
  isStill?: boolean
  lens: VisualizerLens
  onConversationClick?: (conversationId: string) => void
  onSliceHoverChange?: (bucketKey: string | null) => void
  renderUnderlay?: boolean
  sliceFillPalette: string[]
  sliceMarkPalette: string[]
  slices: SetSlice[]
  underlayCanvasRef: RefObject<HTMLCanvasElement | null>
  visualizationKey: string
}

interface HoverState {
  bucketLabel: string
  conversation: VisualizerConversation
  x: number
  y: number
}

const UNDERLAY_PLATE_SCREEN_DIAMETER_RATIO = 0.88
const PLATE_BACKGROUND_COLOR = "var(--surface-contrast)"
const ANOMALY_LENS_KEY = "anomalies"
const ACTIVE_UNDERLAY_LENS_KEYS = new Set(["call-topic", "personas"])
const ACTIVE_UNDERLAY_RADIUS_SCALE = 1.2
interface PersistedOverlayDotMotion {
  anchorU: number
  anchorV: number
  nodalStrength: number
  ringColor: RgbColor
  underlayColor: RgbColor
}

interface OverlayDotMotion extends OverlayDotBlueprint {
  fromAnchorU: number
  fromAnchorV: number
  fromRingColor: RgbColor
  fromUnderlayColor: RgbColor
  ringColor: RgbColor
  scatterU: number
  scatterV: number
  underlayColor: RgbColor
}

export function UploadedConversationsVisualizerConversationOverlay({
  activeBucketKey,
  activeAnomalyMarkerColor,
  className,
  highlightedConversationIds,
  isStill = false,
  lens,
  onConversationClick,
  onSliceHoverChange,
  renderUnderlay = true,
  sliceFillPalette,
  sliceMarkPalette,
  slices,
  underlayCanvasRef,
  visualizationKey,
}: UploadedConversationsVisualizerConversationOverlayProps) {
  const [hoverState, setHoverState] = useState<HoverState | null>(null)
  const hoveredSliceBucketKeyRef = useRef<string | null>(null)
  const underlayHoverZonesRef = useRef<ClusterHoverZone[]>([])
  const svgRef = useRef<SVGSVGElement>(null)
  const anomalyMarkerColorRef = useRef<string | null>(
    activeAnomalyMarkerColor ?? null,
  )
  const activeBucketKeyRef = useRef<string | null>(activeBucketKey ?? null)
  const highlightedConversationIdsRef = useRef(highlightedConversationIds)
  const drawFrameNowRef = useRef<(() => void) | null>(null)
  const isStillRef = useRef(isStill)
  const syncMotionRef = useRef<(() => void) | null>(null)
  const [motionClock] = useState(() =>
    createVisualizerMotionClock(() => isStillRef.current),
  )
  const previousVisualizationKeyRef = useRef(visualizationKey)
  const previousDotsByIdRef = useRef<Map<string, PersistedOverlayDotMotion>>(
    new Map(),
  )
  const transitionGenerationRef = useRef(0)
  const selectedVisualizationKey = visualizationKey

  const dots = useMemo(
    () => resolveOverlayDotBlueprints(slices, lens, visualizationKey),
    [lens, slices, visualizationKey],
  )

  const emitSliceHoverChange = useCallback(
    (bucketKey: string | null) => {
      if (hoveredSliceBucketKeyRef.current === bucketKey) {
        return
      }
      hoveredSliceBucketKeyRef.current = bucketKey
      onSliceHoverChange?.(bucketKey)
    },
    [onSliceHoverChange],
  )

  const resolveBucketKeyFromPointer = useCallback(
    (event: ReactMouseEvent<SVGSVGElement>) => {
      const bounds = event.currentTarget.getBoundingClientRect()
      const pointerX = event.clientX - bounds.left
      const pointerY = event.clientY - bounds.top
      return resolveHoveredBucketKeyFromZones(
        underlayHoverZonesRef.current,
        pointerX,
        pointerY,
      )
    },
    [],
  )

  const handleSvgMouseMove = useCallback(
    (event: ReactMouseEvent<SVGSVGElement>) => {
      const target = event.target
      if (
        target instanceof Element &&
        target.closest(
          "[data-conversation-hit-dot-id],[data-conversation-visible-dot-id]",
        )
      ) {
        return
      }

      setHoverState((current) => (current ? null : current))

      emitSliceHoverChange(resolveBucketKeyFromPointer(event))
    },
    [emitSliceHoverChange, resolveBucketKeyFromPointer],
  )

  const handleDotMouseMove = useCallback(
    (dot: OverlayDotBlueprint, event: ReactMouseEvent<SVGCircleElement>) => {
      const hasAnomalySelection = (highlightedConversationIds?.size ?? 0) > 0
      if (
        (lens.key === ANOMALY_LENS_KEY || lens.key === "failures") &&
        hasAnomalySelection &&
        !(highlightedConversationIds?.has(dot.dotId) ?? false)
      ) {
        setHoverState(null)
        emitSliceHoverChange(null)
        return
      }
      const point = resolveHoverPosition(event)
      setHoverState({
        bucketLabel: dot.bucketLabel,
        conversation: dot.conversation,
        x: point.x,
        y: point.y,
      })
      emitSliceHoverChange(slices[dot.sliceIndex]?.bucket.key ?? null)
    },
    [emitSliceHoverChange, highlightedConversationIds, lens.key, slices],
  )

  useEffect(() => {
    highlightedConversationIdsRef.current = highlightedConversationIds
    anomalyMarkerColorRef.current = activeAnomalyMarkerColor ?? null
    drawFrameNowRef.current?.()
  }, [activeAnomalyMarkerColor, highlightedConversationIds])
  useEffect(() => {
    activeBucketKeyRef.current = activeBucketKey ?? null
    drawFrameNowRef.current?.()
  }, [activeBucketKey])
  useEffect(() => {
    isStillRef.current = isStill
    syncMotionRef.current?.()
  }, [isStill])
  useEffect(() => {
    const svgElement = svgRef.current
    const underlayCanvas = underlayCanvasRef.current
    if (!svgElement || !underlayCanvas || dots.length === 0) {
      underlayHoverZonesRef.current = []
      previousVisualizationKeyRef.current = selectedVisualizationKey
      previousDotsByIdRef.current = new Map()
      return
    }

    const underlayContext = underlayCanvas.getContext("2d")
    if (!underlayContext) {
      underlayHoverZonesRef.current = []
      return
    }

    const visibleDotsById = new Map<string, SVGCircleElement>()
    const hitDotsById = new Map<string, SVGCircleElement>()

    svgElement
      .querySelectorAll<SVGCircleElement>("[data-conversation-visible-dot-id]")
      .forEach((element) => {
        const conversationId = element.dataset.conversationVisibleDotId
        if (!conversationId) {
          return
        }
        visibleDotsById.set(conversationId, element)
      })

    svgElement
      .querySelectorAll<SVGCircleElement>("[data-conversation-hit-dot-id]")
      .forEach((element) => {
        const conversationId = element.dataset.conversationHitDotId
        if (!conversationId) {
          return
        }
        hitDotsById.set(conversationId, element)
      })

    const priorDotsById = previousDotsByIdRef.current
    const shouldAnimateLensTransition =
      // A still plate jumps to the new layout, since no frames follow to animate it.
      !isStillRef.current &&
      previousVisualizationKeyRef.current !== selectedVisualizationKey &&
      priorDotsById.size > 0
    if (shouldAnimateLensTransition) {
      transitionGenerationRef.current += 1
    }
    const transitionSeed = transitionGenerationRef.current
    const defaultColor = { b: 128, g: 128, r: 128 } satisfies RgbColor
    let markPalette = resolveCanvasColorPalette(sliceMarkPalette)
    if (markPalette.length === 0) {
      markPalette = [defaultColor]
    }
    let fillPalette = resolveCanvasColorPalette(sliceFillPalette)
    if (fillPalette.length === 0) {
      fillPalette = markPalette
    }
    const resolvePlateBackgroundColor = () => {
      const [resolvedColor] = resolveCanvasColorPalette([
        PLATE_BACKGROUND_COLOR,
      ])
      return resolvedColor ?? defaultColor
    }
    let plateBackgroundColor = resolvePlateBackgroundColor()
    const resolveActiveAnomalyRingColor = () => {
      const [resolvedColor] = resolveCanvasColorPalette([
        anomalyMarkerColorRef.current ?? PLATE_BACKGROUND_COLOR,
      ])
      return resolvedColor ?? plateBackgroundColor
    }
    let activeAnomalyRingColor = resolveActiveAnomalyRingColor()

    let lensTransitionStart = shouldAnimateLensTransition ? motionClock() : null
    let underlayWidth = 0
    let underlayHeight = 0
    let underlayDpr = 1

    const syncUnderlayCanvasResolution = () => {
      const nextWidth = Math.max(1, Math.floor(underlayCanvas.clientWidth))
      const nextHeight = Math.max(1, Math.floor(underlayCanvas.clientHeight))
      const nextDpr = Math.max(1, window.devicePixelRatio || 1)
      if (
        nextWidth === underlayWidth &&
        nextHeight === underlayHeight &&
        nextDpr === underlayDpr
      ) {
        return
      }

      underlayWidth = nextWidth
      underlayHeight = nextHeight
      underlayDpr = nextDpr
      underlayCanvas.width = Math.round(underlayWidth * underlayDpr)
      underlayCanvas.height = Math.round(underlayHeight * underlayDpr)
      underlayContext.setTransform(underlayDpr, 0, 0, underlayDpr, 0, 0)
    }

    const motionDots = dots.map((dot, dotIndex) => {
      const scatter = computeBackgroundScatterAnchor(dotIndex, transitionSeed)
      const priorDot = priorDotsById.get(dot.dotId)
      const ringColor =
        markPalette[dot.sliceIndex % markPalette.length] ?? markPalette[0]
      const underlayColor =
        fillPalette[dot.sliceIndex % fillPalette.length] ?? fillPalette[0]

      return {
        ...dot,
        fromAnchorU: priorDot?.anchorU ?? dot.anchorU,
        fromAnchorV: priorDot?.anchorV ?? dot.anchorV,
        fromRingColor: priorDot?.ringColor ?? ringColor,
        fromUnderlayColor: priorDot?.underlayColor ?? underlayColor,
        ringColor,
        scatterU: scatter.u,
        scatterV: scatter.v,
        underlayColor,
      } satisfies OverlayDotMotion
    })

    let animationFrame = 0

    const drawFrame = (timestamp: number) => {
      syncUnderlayCanvasResolution()

      const time = timestamp * 0.0012
      const transitionProgress =
        lensTransitionStart === null
          ? 1
          : clampNumber(
              (timestamp - lensTransitionStart) / LENS_TRANSITION_DURATION_MS,
              0,
              1,
            )
      if (transitionProgress >= 1) {
        lensTransitionStart = null
      }

      underlayContext.clearRect(0, 0, underlayWidth, underlayHeight)
      const underlayPlateDiameter =
        Math.min(underlayWidth, underlayHeight) *
        UNDERLAY_PLATE_SCREEN_DIAMETER_RATIO
      const hasConversationHighlightFilter =
        Boolean(highlightedConversationIdsRef.current) &&
        (highlightedConversationIdsRef.current?.size ?? 0) > 0
      const isAnomaliesLens = lens.key === ANOMALY_LENS_KEY
      const isAmbientNoLens = lens.key === NO_LENS_VISUALIZER_LENS_KEY
      const shouldScaleActiveBucketUnderlay = ACTIVE_UNDERLAY_LENS_KEYS.has(
        lens.key,
      )
      const activeBucketKey = activeBucketKeyRef.current
      if (isAnomaliesLens && hasConversationHighlightFilter) {
        activeAnomalyRingColor = resolveActiveAnomalyRingColor()
      }
      const clusterUnderlayAggregates = new Map<
        number,
        ClusterUnderlayAggregate
      >()
      const inactiveUnderlayDrawInstructions: UnderlayDrawInstruction[] = []
      const activeUnderlayDrawInstructions: UnderlayDrawInstruction[] = []

      for (const dot of motionDots) {
        const isHighlightedConversation =
          highlightedConversationIdsRef.current?.has(dot.dotId) ?? false
        const isTransitioning =
          lensTransitionStart !== null && transitionProgress < 1
        const chaosPoint = computeBackgroundChaosPoint(dot, time)
        const scatterPoint = computeBackgroundScatterPoint(dot, time)

        let driftPoint
        let releaseMix = 0
        let regroupMix = 0

        if (isTransitioning && transitionProgress < LENS_RELEASE_PHASE_SHARE) {
          releaseMix = easeOutCubic(
            clampNumber(transitionProgress / LENS_RELEASE_PHASE_SHARE, 0, 1),
          )
          driftPoint = clampToPlate(
            dot.fromAnchorU + (scatterPoint.u - dot.fromAnchorU) * releaseMix,
            dot.fromAnchorV + (scatterPoint.v - dot.fromAnchorV) * releaseMix,
          )
        } else if (isTransitioning) {
          regroupMix = easeInOutSine(
            clampNumber(
              (transitionProgress - LENS_RELEASE_PHASE_SHARE) /
                (1 - LENS_RELEASE_PHASE_SHARE),
              0,
              1,
            ),
          )
          driftPoint = clampToPlate(
            scatterPoint.u + (dot.anchorU - scatterPoint.u) * regroupMix,
            scatterPoint.v + (dot.anchorV - scatterPoint.v) * regroupMix,
          )
        } else {
          const settleMix = 0.82 + Math.sin(time * 0.32 + dot.phaseB) * 0.06
          const driftScale = 1 - settleMix
          driftPoint = clampToPlate(
            dot.anchorU + (chaosPoint.u - dot.anchorU) * driftScale,
            dot.anchorV + (chaosPoint.v - dot.anchorV) * driftScale,
          )
        }

        const localJitter = 0.0009 + dot.amp * 0.0012
        const transitionTurbulence = isTransitioning
          ? (0.0028 + dot.amp * 0.0034) * Math.sin(transitionProgress * Math.PI)
          : 0
        const animatedPoint = clampToPlate(
          driftPoint.u +
            Math.cos(time * (2.45 + dot.frequencyA) + dot.phaseA) *
              localJitter +
            Math.sin(time * (5.05 + dot.frequencyB) + dot.phaseB) *
              transitionTurbulence,
          driftPoint.v +
            Math.sin(time * (2.2 + dot.frequencyB) + dot.phaseB) * localJitter +
            Math.cos(time * (4.7 + dot.frequencyA) + dot.phaseA) *
              transitionTurbulence,
        )
        const projected = projectToScreen(
          animatedPoint.u,
          animatedPoint.v,
          100,
          100,
        )
        const underlayProjected = projectToScreen(
          animatedPoint.u,
          animatedPoint.v,
          underlayWidth,
          underlayHeight,
        )
        const visibleDot = visibleDotsById.get(dot.dotId)
        const hitDot = hitDotsById.get(dot.dotId)
        const blendedMix = isTransitioning
          ? easeInOutSine(transitionProgress)
          : 1
        const ringColor = resolveBlendedColor(
          dot.fromRingColor,
          dot.ringColor,
          blendedMix,
        )
        const underlayColor = resolveBlendedColor(
          dot.fromUnderlayColor,
          dot.underlayColor,
          blendedMix,
        )
        const borderPlateBlendMix = isTransitioning
          ? easeInOutSine(Math.sin(transitionProgress * Math.PI))
          : 0
        const effectiveRingColor = isAmbientNoLens
          ? plateBackgroundColor
          : hasConversationHighlightFilter && !isHighlightedConversation
            ? plateBackgroundColor
            : isAnomaliesLens && hasConversationHighlightFilter
              ? activeAnomalyRingColor
              : ringColor
        const borderColor = resolveBlendedColor(
          effectiveRingColor,
          plateBackgroundColor,
          borderPlateBlendMix,
        )

        if (
          renderUnderlay &&
          (!hasConversationHighlightFilter || isHighlightedConversation)
        ) {
          const isBucketActiveForDot =
            activeBucketKey !== null &&
            slices[dot.sliceIndex]?.bucket.key === activeBucketKey
          const underlayRadius =
            underlayPlateDiameter *
            (0.092 + dot.nodalStrength * 0.018) *
            (shouldScaleActiveBucketUnderlay && isBucketActiveForDot
              ? ACTIVE_UNDERLAY_RADIUS_SCALE
              : 1)
          const underlayTransitionAlpha = resolveUnderlayTransitionAlpha(
            isTransitioning,
            transitionProgress,
          )
          const underlayCenterAlpha = clampNumber(
            (0.22 + dot.nodalStrength * 0.1) * underlayTransitionAlpha,
            0,
            0.52,
          )
          upsertClusterUnderlayAggregate(
            clusterUnderlayAggregates,
            dot.sliceIndex,
            underlayProjected.x,
            underlayProjected.y,
            underlayRadius,
          )
          const drawInstruction = {
            centerAlpha: underlayCenterAlpha,
            color: underlayColor,
            radius: underlayRadius,
            x: underlayProjected.x,
            y: underlayProjected.y,
          } satisfies UnderlayDrawInstruction

          if (shouldScaleActiveBucketUnderlay && isBucketActiveForDot) {
            activeUnderlayDrawInstructions.push(drawInstruction)
          } else {
            inactiveUnderlayDrawInstructions.push(drawInstruction)
          }
        }

        syncOverlayDotElements({
          borderColor,
          hasConversationHighlightFilter,
          hitDot,
          isHighlightedConversation,
          projected,
          visibleDot,
        })
      }

      for (const instruction of inactiveUnderlayDrawInstructions) {
        drawUnderlayInstruction(underlayContext, instruction)
      }
      for (const instruction of activeUnderlayDrawInstructions) {
        drawUnderlayInstruction(underlayContext, instruction)
      }

      underlayHoverZonesRef.current = buildClusterHoverZones(
        clusterUnderlayAggregates,
        slices,
      )
    }

    const frame = () => {
      drawFrame(motionClock())
      animationFrame = window.requestAnimationFrame(frame)
    }
    drawFrameNowRef.current = () => {
      drawFrame(motionClock())
    }

    const refreshPalette = () => {
      const nextMarkPalette = resolveCanvasColorPalette(sliceMarkPalette)
      if (nextMarkPalette.length > 0) {
        markPalette = nextMarkPalette
      }
      const nextFillPalette = resolveCanvasColorPalette(sliceFillPalette)
      if (nextFillPalette.length > 0) {
        fillPalette = nextFillPalette
      }
      plateBackgroundColor = resolvePlateBackgroundColor()
      activeAnomalyRingColor = resolveActiveAnomalyRingColor()
      for (const dot of motionDots) {
        dot.ringColor =
          markPalette[dot.sliceIndex % markPalette.length] ?? markPalette[0]
        dot.underlayColor =
          fillPalette[dot.sliceIndex % fillPalette.length] ?? fillPalette[0]
      }
      drawFrame(motionClock())
    }

    drawFrame(motionClock())
    if (!isStillRef.current) {
      animationFrame = window.requestAnimationFrame(frame)
    }

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            drawFrame(motionClock())
          })
    resizeObserver?.observe(svgElement)

    const themeObserver = new MutationObserver(() => {
      refreshPalette()
    })
    themeObserver.observe(document.documentElement, {
      attributeFilter: ["class", "data-theme"],
      attributes: true,
    })

    syncMotionRef.current = () => {
      window.cancelAnimationFrame(animationFrame)
      drawFrame(motionClock())
      if (!isStillRef.current) {
        animationFrame = window.requestAnimationFrame(frame)
      }
    }

    return () => {
      syncMotionRef.current = null
      resizeObserver?.disconnect()
      themeObserver.disconnect()
      window.cancelAnimationFrame(animationFrame)
      underlayHoverZonesRef.current = []
      drawFrameNowRef.current = null
      previousVisualizationKeyRef.current = selectedVisualizationKey
      previousDotsByIdRef.current = new Map(
        motionDots.map((dot) => [
          dot.dotId,
          {
            anchorU: dot.anchorU,
            anchorV: dot.anchorV,
            nodalStrength: dot.nodalStrength,
            ringColor: dot.ringColor,
            underlayColor: dot.underlayColor,
          },
        ]),
      )
    }
  }, [
    dots,
    lens.key,
    motionClock,
    renderUnderlay,
    selectedVisualizationKey,
    sliceFillPalette,
    sliceMarkPalette,
    slices,
    underlayCanvasRef,
  ])

  return (
    <div
      className={cn("absolute inset-0", className)}
      data-testid="uploaded-conversations-conversation-overlay"
    >
      <svg
        ref={svgRef}
        viewBox="0 0 100 100"
        className="relative h-full w-full"
        data-testid="uploaded-conversations-conversation-overlay-svg"
        onMouseMove={handleSvgMouseMove}
        onMouseLeave={() => {
          setHoverState(null)
          emitSliceHoverChange(null)
        }}
      >
        <UploadedConversationsVisualizerDotField
          activeAnomalyMarkerColor={activeAnomalyMarkerColor}
          dots={dots}
          highlightedConversationIds={highlightedConversationIds}
          isAmbientLensSelected={lens.key === NO_LENS_VISUALIZER_LENS_KEY}
          isSelectionLensSelected={
            lens.key === ANOMALY_LENS_KEY || lens.key === "failures"
          }
          onConversationClick={onConversationClick}
          onDotMouseMove={handleDotMouseMove}
          sliceMarkPalette={sliceMarkPalette}
        />
      </svg>

      {hoverState && typeof document !== "undefined" ? (
        <UploadedConversationsVisualizerOverlayHoverCard
          bucketLabel={hoverState.bucketLabel}
          firstSentence={hoverState.conversation.firstSentence}
          x={hoverState.x}
          y={hoverState.y}
        />
      ) : null}
    </div>
  )
}
